import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import { Container } from '@/components/layout/Container';
import { BrandMark } from './BrandMark';
import {
  FOOTER_LEGAL_LINKS,
  FOOTER_PRODUCT_LINKS,
  FOOTER_RESOURCES_LINKS,
  type NavLinkItem,
} from './nav-links';

interface FooterColumn {
  titleKey: ParseKeys<'nav'>;
  links: readonly NavLinkItem[];
}

const COLUMNS: readonly FooterColumn[] = [
  { titleKey: 'footer.columns.product', links: FOOTER_PRODUCT_LINKS },
  { titleKey: 'footer.columns.resources', links: FOOTER_RESOURCES_LINKS },
  { titleKey: 'footer.columns.legal', links: FOOTER_LEGAL_LINKS },
] as const;

export function Footer() {
  const { t } = useTranslation(['nav']);
  const year = new Date().getFullYear();

  return (
    <footer
      aria-label={t('nav:footer.label')}
      className="border-t border-border bg-background"
    >
      <Container size="xl">
        <div className="grid gap-10 py-14 md:grid-cols-4">
          <div className="md:col-span-1">
            <BrandMark />
            <p className="mt-4 max-w-xs text-sm text-muted-foreground">
              {t('nav:footer.tagline')}
            </p>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.titleKey}>
              <h2 className="text-sm font-semibold text-foreground">
                {t(col.titleKey, { ns: 'nav' })}
              </h2>
              <ul className="mt-4 flex flex-col gap-3">
                {col.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-sm text-muted-foreground transition-colors duration-fast ease-out-quart hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand focus-visible:rounded-sm"
                    >
                      {t(link.i18nKey, { ns: 'nav' })}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-col items-start justify-between gap-4 border-t border-border py-6 sm:flex-row sm:items-center">
          <p className="text-xs text-muted-foreground">
            {t('nav:footer.copyright', { year })}
          </p>
        </div>
      </Container>
    </footer>
  );
}
